import { Box } from '@mui/material';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import { Typography } from 'src/components/new/Typography';
import PricingTable from 'src/components/table/PricingTable';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LockedInput from '../pricing-inputs/LockedInput';
import { mapPostageType } from './FirstClassPostageRates';

const RATE_FIELDS = ['certified_domestic'] as const;

const COLLAPSABLE_6X9 = new Set(['2', '3']);
const COLLAPSABLE_FLAT = new Set(['5', '6', '7', '8', '9', '10', '11', '12', '13']);

interface RateCell {
  id: number;
  price: number;
}

interface Row {
  displayWeight: string;
  rawWeight: string;
  packageType: string;
  sheetRange: React.ReactNode;
  [key: string]: any;
}

const getDisplayWeight = (weight: string, is6x9Collapsed: boolean, isFlatCollapsed: boolean, packageType: string) => {
  if (weight === '1' && !is6x9Collapsed) return '1 - 3';
  if (weight === '4' && !isFlatCollapsed) return '4 - 13';

  if (packageType === 'flat_rate_envelope') return '13 - 21';
  if (packageType === 'legal_flat_rate_envelope') return '21 - 24';

  return weight;
};

const getSheetRange = (weight: string, is6x9Collapsed: boolean, isFlatCollapsed: boolean, packageType?: string) => {
  if (packageType === 'flat_rate_envelope')
    return (
      <>
        73 → <b>120</b>
      </>
    );

  if (packageType === 'legal_flat_rate_envelope')
    return (
      <>
        121 → <b>140</b>
      </>
    );

  if (packageType === 'flat_rate_box')
    return (
      <>
        141 → <b>500</b>
      </>
    );

  switch (weight) {
    case '1':
      return is6x9Collapsed ? (
        '1 →'
      ) : (
        <>
          1 → <b>16</b>
        </>
      );

    case '3':
      return <b>16</b>;

    case '4':
      return isFlatCollapsed ? (
        '17 →'
      ) : (
        <>
          17 → <b>72</b>
        </>
      );

    case '13':
      return <b>72</b>;

    default:
      return '-';
  }
};

const CertifiedDomesticPostageRates: React.FC = () => {
  const postageRates = useSelector((state: RootState) => state.admin.sortedPostageRatesV2);

  const [is6x9Collapsed, setIs6x9Collapsed] = useState(false);
  const [isFlatCollapsed, setIsFlatCollapsed] = useState(false);

  const [data, setData] = useState<Row[]>([]);

  useMemo(() => {
    const encounteredPackageTypes = new Set();
    const rows: Row[] = Object.entries(postageRates.certified ?? {})
      .map(([weight, rates]: any) => {
        if (!rates?.length) return null;

        const packageType = rates[0].packageType;

        if (
          (weight !== '14' && packageType === 'flat_rate_envelope') ||
          (weight !== '22' && packageType === 'legal_flat_rate_envelope')
        ) {
          return null;
        }

        const row: Row = {
          rawWeight: weight,
          displayWeight: getDisplayWeight(weight, is6x9Collapsed, isFlatCollapsed, packageType),
          packageType,
          sheetRange: getSheetRange(weight, is6x9Collapsed, isFlatCollapsed, packageType),
          hasBottomBorder: shouldHaveBorder(weight, is6x9Collapsed, isFlatCollapsed, packageType),
        };

        rates.forEach((r: any) => {
          if (r.packageType === packageType) {
            row[r.mailingType] = { id: r.id, price: r.price };
          }
        });
        encounteredPackageTypes.add(packageType);
        return row;
      })
      .filter(Boolean) as Row[];

    const filtered = rows.filter((r) => {
      if (!is6x9Collapsed && COLLAPSABLE_6X9.has(r.rawWeight)) return false;
      if (!isFlatCollapsed && COLLAPSABLE_FLAT.has(r.rawWeight)) return false;
      return true;
    });

    setData(filtered);
  }, [postageRates, is6x9Collapsed, isFlatCollapsed]);

  const renderCell = (row: Row, column: any) => {
    if (RATE_FIELDS.includes(column.field)) {
      const cell: RateCell = row[column.field];

      return <LockedInput value={`$${(cell?.price ?? 0).toFixed(2)}`} />;
    }

    if (column.field === 'packageType') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ p: 1, borderRadius: '999px', backgroundColor: '#f0f6ff' }}>
            <Typography fontWeight={500} fontSize={14}>
              {mapPostageType[row.packageType as keyof typeof mapPostageType]}
            </Typography>
          </Box>

          {(row.displayWeight === '1 - 3' || row.displayWeight === '1') &&
            (is6x9Collapsed ? (
              <KeyboardArrowUpIcon onClick={() => setIs6x9Collapsed(false)} sx={{ cursor: 'pointer' }} />
            ) : (
              <KeyboardArrowDownIcon onClick={() => setIs6x9Collapsed(true)} sx={{ cursor: 'pointer' }} />
            ))}

          {(row.displayWeight === '4 - 13' || row.displayWeight === '4') &&
            (isFlatCollapsed ? (
              <KeyboardArrowUpIcon onClick={() => setIsFlatCollapsed(false)} sx={{ cursor: 'pointer' }} />
            ) : (
              <KeyboardArrowDownIcon onClick={() => setIsFlatCollapsed(true)} sx={{ cursor: 'pointer' }} />
            ))}
        </Box>
      );
    }

    return row[column.field];
  };

  return <PricingTable mainHeaderName="Certified" columns={domesticTableColumns} data={data} renderCell={renderCell} />;
};

export default CertifiedDomesticPostageRates;

const domesticTableColumns = [
  {
    headerName: (
      <>
        <p style={{ margin: 0 }}>Weight</p>
        <p style={{ margin: 0, color: '#7e828b' }}>Not Over (oz)</p>
      </>
    ),
    field: 'displayWeight',
    width: 70,
  },
  { headerName: 'Sheet Range', field: 'sheetRange', width: 50 },
  {
    headerName: 'Package',
    field: 'packageType',
    customHeaderStyle: { borderRight: '1px solid rgba(224,224,224,1)' },
    customCellStyle: { borderRight: '1px solid rgba(224,224,224,1)' },
    width: 120,
  },
  {
    headerName: 'Domestic',
    field: 'certified_domestic',
    width: 360,
    customHeaderStyle: { textAlign: 'center' as const },
  },
];

const shouldHaveBorder = (weight: string, is6x9Collapsed: boolean, isFlatCollapsed: boolean, packageType: string) => {
  if (weight === '1' && !is6x9Collapsed) return true;
  if (weight === '3' && is6x9Collapsed) return true;
  if (weight === '4' && !isFlatCollapsed) return true;
  if (weight === '13' && isFlatCollapsed) return true;
  if (['flat_rate_envelope', 'legal_flat_rate_envelope', 'flat_rate_box'].includes(packageType)) return true;
  return false;
};
 